import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { Star, Quote } from "lucide-react";

/**
 * LandingTestimonials — social proof after the metrics band.
 * 3 fictional-but-realistic Turkish/EN team quotes. No external images.
 * Avatars = initials on gradient chips. Linear-style, minimal, no glass overload.
 */

const TESTIMONIALS = [
  {
    id: "elif",
    initials: "EK",
    gradient: "from-violet-500 to-pink-500",
    accent: "border-violet-200",
    quoteKey: "landing.testimonials.q1.text",
    nameKey: "landing.testimonials.q1.name",
    roleKey: "landing.testimonials.q1.role",
    orgKey: "landing.testimonials.q1.org",
    featured: true,
  },
  {
    id: "ali",
    initials: "AY",
    gradient: "from-amber-500 to-orange-500",
    accent: "border-amber-200",
    quoteKey: "landing.testimonials.q2.text",
    nameKey: "landing.testimonials.q2.name",
    roleKey: "landing.testimonials.q2.role",
    orgKey: "landing.testimonials.q2.org",
  },
  {
    id: "merve",
    initials: "MÖ",
    gradient: "from-emerald-500 to-teal-500",
    accent: "border-emerald-200",
    quoteKey: "landing.testimonials.q3.text",
    nameKey: "landing.testimonials.q3.name",
    roleKey: "landing.testimonials.q3.role",
    orgKey: "landing.testimonials.q3.org",
  },
];

export function LandingTestimonials() {
  const { t } = useTranslation();
  const reduced = useReducedMotion();

  return (
    <section
      className="relative overflow-hidden bg-white py-20 md:py-28"
      data-testid="landing-testimonials"
    >
      {/* Subtle grid overlay */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(124,58,237,0.06),transparent_60%)]"
      />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto max-w-2xl text-center"
        >
          <div
            className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 shadow-sm"
            data-testid="landing-testimonials-eyebrow"
          >
            <Star className="h-3 w-3 fill-emerald-500 text-emerald-500" />
            {t("landing.testimonials.eyebrow")}
          </div>
          <h2
            className="font-display text-3xl font-extrabold leading-tight tracking-tight text-zinc-950 sm:text-4xl md:text-5xl"
            data-testid="landing-testimonials-title"
          >
            {t("landing.testimonials.title_prefix")}
            <span className="serpix-gradient-text">
              {t("landing.testimonials.title_accent")}
            </span>
            {t("landing.testimonials.title_suffix")}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-zinc-600">
            {t("landing.testimonials.subtitle")}
          </p>
        </motion.div>

        {/* Grid */}
        <div className="mt-14 grid gap-6 md:grid-cols-3 md:gap-6 lg:gap-8">
          {TESTIMONIALS.map((tst, idx) => (
            <TestimonialCard
              key={tst.id}
              tst={tst}
              t={t}
              reduced={reduced}
              delay={0.1 + idx * 0.12}
            />
          ))}
        </div>

        {/* Foot note */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[12px] font-medium text-zinc-500"
          data-testid="landing-testimonials-footnote"
        >
          <div className="flex items-center gap-1.5">
            <div className="flex -space-x-1.5">
              {["#7c3aed", "#ec4899", "#f59e0b", "#10b981", "#3b82f6"].map(
                (c) => (
                  <span
                    key={c}
                    className="inline-block h-5 w-5 rounded-full border-2 border-white"
                    style={{ background: c }}
                  />
                ),
              )}
            </div>
            <span>{t("landing.testimonials.foot_users")}</span>
          </div>
          <span className="hidden h-4 w-px bg-zinc-200 md:block" />
          <div className="flex items-center gap-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star
                key={i}
                className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
              />
            ))}
            <span className="ml-1">{t("landing.testimonials.foot_rating")}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TestimonialCard({ tst, t, reduced, delay }) {
  return (
    <motion.figure
      initial={reduced ? false : { opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      whileHover={reduced ? undefined : { y: -6 }}
      className={
        "group relative flex h-full flex-col justify-between rounded-2xl border bg-white p-6 shadow-sm transition-shadow hover:shadow-xl hover:shadow-violet-500/10 md:p-7 " +
        tst.accent +
        (tst.featured ? " md:col-span-1 md:row-span-1" : "")
      }
      data-testid={`landing-testimonial-${tst.id}`}
    >
      {/* Decorative quote */}
      <Quote
        aria-hidden="true"
        className="absolute right-5 top-5 h-8 w-8 text-zinc-100 transition-colors group-hover:text-violet-100"
      />

      <div className="relative">
        {/* Stars */}
        <div className="mb-3 flex items-center gap-0.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star
              key={i}
              className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
            />
          ))}
        </div>
        {/* Quote text */}
        <blockquote className="text-[15px] leading-relaxed text-zinc-800 md:text-base">
          &ldquo;{t(tst.quoteKey)}&rdquo;
        </blockquote>
      </div>

      {/* Author */}
      <figcaption className="mt-6 flex items-center gap-3 border-t border-zinc-100 pt-4">
        <div
          className={
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-sm font-bold text-white shadow-md " +
            tst.gradient
          }
          aria-hidden="true"
        >
          {tst.initials}
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-zinc-900">
            {t(tst.nameKey)}
          </div>
          <div className="truncate text-[12px] text-zinc-500">
            <span className="font-medium text-zinc-700">{t(tst.roleKey)}</span>
            <span className="mx-1 text-zinc-300">·</span>
            <span>{t(tst.orgKey)}</span>
          </div>
        </div>
      </figcaption>
    </motion.figure>
  );
}
